import { useRef } from 'react'
import type { KeyboardEvent } from 'react'

/** What a component wires onto each of its options to make the group rove. */
export interface RovingRadioGroup {
  /** The ref the group focuses through — one per option, by index. */
  optionRef: (index: number) => (node: HTMLButtonElement | null) => void
  /** The option's `onKeyDown`. Arrows move and select; Home/End reach the ends. */
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>, index: number) => void
}

/**
 * The KEYBOARD half of a radiogroup, written once for the three the club draws
 * — the register's `SegmentedControl`, the stage's hole rail, and the gallery's
 * grid of thumbnails.
 *
 * ## Why this is one function and not three
 *
 * All three are the same widget: exactly one of N is chosen, and choosing
 * re-draws what is already on the page. The W3C pattern for that is a
 * radiogroup with a ROVING TABINDEX — the group takes ONE tab stop, carried by
 * the chosen option; the arrows move focus AND selection together (the native
 * radio behaviour); Home and End reach the ends; Tab leaves the group entirely
 * rather than walking it. Eighteen tab stops on the hole rail would make a
 * keyboard user cross the whole card to get past the picture.
 *
 * It was hand-rolled twice, byte for byte, before this existed. Two copies of
 * a keyboard contract is how one of them quietly loses `Home` — so the contract
 * lives here and the components own only what differs: their DOM, their
 * classes, and what "the option at index N" means to them.
 *
 * ## The seam is an INDEX, deliberately
 *
 * `onSelect` is handed the index of the option to choose, never a value: the
 * rail's options are hole numbers, the segmented control's are strings, and the
 * gallery's are the subset of holes that have a drawing. An index is the one
 * thing all three already agree on, and it keeps the hook free of every shape
 * it would otherwise have to be generic over.
 *
 * The caller still owns the two ARIA halves this cannot see: `role="radiogroup"`
 * with a label on the container, and `role="radio"` + `aria-checked` +
 * `tabIndex={checked ? 0 : -1}` on each option. This hook moves focus; the
 * markup says what was moved between.
 *
 * @param length how many options the group renders — the wrap point
 * @param onSelect called with the index the member moved to, before it is focused
 */
export function useRovingRadioGroup(
  length: number,
  onSelect: (index: number) => void,
): RovingRadioGroup {
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  const move = (from: number, to: number) => {
    // A group with no options has nothing to rove between — and `% 0` is NaN,
    // which would select an option that does not exist rather than do nothing.
    if (length === 0) return
    // Modulo BOTH ways: right from the last lands on the first and left from
    // the first lands on the last, which is what a radio group does.
    const index = ((to % length) + length) % length
    if (index === from) return
    onSelect(index)
    refs.current[index]?.focus()
  }

  return {
    optionRef: (index: number) => (node: HTMLButtonElement | null) => {
      refs.current[index] = node
    },
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault()
          move(index, index + 1)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          move(index, index - 1)
          break
        case 'Home':
          event.preventDefault()
          move(index, 0)
          break
        case 'End':
          event.preventDefault()
          move(index, length - 1)
          break
      }
    },
  }
}
